import { useEffect, useState } from "react";
import { RefreshCw, X } from "lucide-react";

type UpdateSW = (reloadPage?: boolean) => Promise<void>;

export function PwaUpdatePrompt() {
  const [needRefresh, setNeedRefresh] = useState(false);
  const [updateSW, setUpdateSW] = useState<UpdateSW | null>(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!("serviceWorker" in navigator)) return;
    if (!import.meta.env.PROD) return;

    // Skip in iframes / preview hosts
    let inIframe = false;
    try {
      inIframe = window.self !== window.top;
    } catch {
      inIframe = true;
    }
    const host = window.location.hostname;
    const isPreview =
      host.includes("id-preview--") ||
      host.includes("lovableproject.com");
    if (inIframe || isPreview) return;

    let cancelled = false;
    import("virtual:pwa-register")
      .then(({ registerSW }) => {
        if (cancelled) return;
        const update = registerSW({
          immediate: true,
          onNeedRefresh() {
            setNeedRefresh(true);
          },
          onRegisteredSW(_swUrl, registration) {
            // Poll every 60s for a new SW so users get prompted promptly.
            if (registration) {
              setInterval(() => {
                registration.update().catch(() => {});
              }, 60 * 1000);
            }
          },
        });
        setUpdateSW(() => update);
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, []);

  if (!needRefresh || dismissed) return null;

  const handleReload = async () => {
    if (updateSW) {
      await updateSW(true);
    } else {
      window.location.reload();
    }
  };

  return (
    <div className="fixed inset-x-3 bottom-3 z-[100] mx-auto max-w-md rounded-xl border border-primary/30 bg-card/95 p-3 shadow-2xl backdrop-blur-md sm:inset-x-auto sm:right-4">
      <div className="flex items-center gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/15 text-primary">
          <RefreshCw className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-foreground">Update available</p>
          <p className="text-xs text-muted-foreground">
            A new version of NexaFX is ready to install.
          </p>
        </div>
        <button
          type="button"
          onClick={handleReload}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground transition-all hover:brightness-110"
        >
          Reload
        </button>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          aria-label="Dismiss"
          className="text-muted-foreground hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
